import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import queryString from 'query-string';
import { all, any, assoc, clone, filter, identity, map, pick, pipe, propEq, propSatisfies, values, when } from 'ramda';
import { useSelector } from 'react-redux';

import Button from 'components/shared/Button';
import CheckBox from 'components/shared/FormAdapters/FormDefault/CheckBox';
import { SNACKBAR_SKIN } from 'components/shared/Snackbar';
import Tooltip, { TooltipText } from 'components/shared/Tooltip';
import { LIBRARY_CONTENT, LIBRARY_ITINERARIES } from 'constants/index';
import { ITINERARIES_ITEM_STEP_PATH, SAMPLE_PATH } from 'constants/routes';
import { GENERAL_ERROR_MESSAGE } from 'constants/texts';
import { useAccountPath } from 'hooks/useAccountPath';
import { showSnackbar } from 'hooks/useSnackbar';
import { accountIdSelector } from 'store/user/selectors';
import { confirmAlert } from 'utils/alert';
import axiosApi from 'utils/axios';
import { capitalize } from 'utils/common';

import { CONTENT_PAGE_PREVIEW_PARAM_NAME, PROTECTIVE_WORD } from './constants';
import s from './index.module.css';

export function useApplyUpdates({ pageId, onCancel, onForceRerender }) {
  const accountId = useSelector(accountIdSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isProtectiveWordEntered, setIsProtectiveWordEntered] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [queryParams, setQueryParams] = useState(null);
  const [isAllItemsChecked, setIsAllItemsChecked] = useState(false);
  const [isAnyItemChecked, setIsAnyItemChecked] = useState(false);
  const getPath = useAccountPath();
  const checkedItemsMap = useRef({});

  const fetchData = useCallback(async () => {
    if (!queryParams) return;

    setIsLoading(true);

    const query = queryString.stringify(queryParams);

    try {
      const data = await axiosApi.get(`acc/${accountId}/pages/${pageId}/proposals?${query}`);

      setItems(processItemsWithCheckMap(data.data.data, checkedItemsMap.current));
      setMeta(data.data.meta);
    } catch (err) {
      // 403 error - means that no updates available
      if (err.errors?.status === 403) {
        setItems([]);

        return;
      }

      showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
    } finally {
      setIsLoading(false);
    }
  }, [accountId, pageId, queryParams]);

  const handleType = useCallback((evt) => {
    setInputValue(evt.target.value.toUpperCase());
  }, []);

  const handleCancel = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    async function confirmApply() {
      setIsLoading(true);

      const proposals = pipe(values, filter(identity), map(onlyIdAndType))(checkedItemsMap.current);

      if (proposals.length) {
        try {
          await axiosApi.patch(`acc/${accountId}/pages/${pageId}/proposals`, { proposals });

          onForceRerender();

          showSnackbar({ text: 'Updates have been successfully applied' });
        } catch (err) {
          showSnackbar({ text: GENERAL_ERROR_MESSAGE, type: SNACKBAR_SKIN.ERROR });
        }
      }
    }

    confirmApply();
  }, [accountId, pageId, onForceRerender]);

  const handleCheckItem = useCallback(
    (id, isChecked) => {
      const itemIndex = items.findIndex((it) => it.id === id);

      if (itemIndex > -1) {
        const newItems = clone(items);
        newItems[itemIndex].isChecked = isChecked;
        checkedItemsMap.current[newItems[itemIndex].id] = isChecked && newItems[itemIndex];

        setItems(newItems);
      }
    },
    [items],
  );

  const handleCheckAllItems = useCallback(() => {
    const toggleItem = (item) => {
      checkedItemsMap.current[item.id] = !isAllItemsChecked && item;

      return assoc('isChecked', !isAllItemsChecked, item);
    };
    const newItems = map(toggleItem, items);

    setItems(newItems);
  }, [items, isAllItemsChecked]);

  const handleClickView = useCallback(
    ({ row }) => {
      const { id, sample, type } = row?.original;
      const tail = getPath(sample ? SAMPLE_PATH : ITINERARIES_ITEM_STEP_PATH, {
        [sample ? 'pageId' : 'itemId']: id,
        libType: sample ? LIBRARY_CONTENT : LIBRARY_ITINERARIES,
        itemType: type.replace('_', '-'),
        step: 'landing',
      });

      window.open(location.origin + tail + `?${CONTENT_PAGE_PREVIEW_PARAM_NAME}=${pageId}`, '_blank');
    },
    [getPath, pageId],
  );

  const handleIgnore = useCallback(
    ({ row }) => {
      confirmAlert({
        title: 'Are you sure you want to skip this update?',
        onConfirm: async () => {
          try {
            setIsLoading(true);

            const { id, type } = row.original;

            await axiosApi.post(`acc/${accountId}/pages/${pageId}/proposals/${id}/ignore`, { type });
            await fetchData();
          } catch (err) {
            showSnackbar({ text: 'Failed to ignore proposal updates', type: SNACKBAR_SKIN.ERROR });
          } finally {
            setIsLoading(false);
          }
        },
      });
    },
    [accountId, pageId, fetchData],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns = useMemo(() => {
    return [
      {
        Header: () => (
          <div data-tip data-for="toggleAll" className={s.checkboxCell}>
            <CheckBox
              className={s.checkboxLabel}
              checkboxClassName={s.checkbox}
              onChange={() => handleCheckAllItems()}
              checked={isAllItemsChecked}
              noMargin
              size="sm"
              disabled={!items.length}
            />
            <Tooltip id="toggleAll" className={s.tooltip}>
              Mark all check-boxes on the current page
            </Tooltip>
          </div>
        ),
        accessor: 'isChecked',
        fixedSize: '3.5rem',
        center: true,
        Cell: ({ row }) => (
          <div>
            <CheckBox
              className={s.checkboxLabel}
              checkboxClassName={s.checkbox}
              onChange={() => handleCheckItem(row?.original?.id, !row?.original?.isChecked)}
              checked={Boolean(row?.original?.isChecked)}
              noMargin
              size="sm"
            />
          </div>
        ),
      },
      {
        Header: () => <div>Proposals & Samples</div>,
        accessor: 'title',
        disableSortBy: true,
        Cell: ({ row }) => {
          const { sample, type, status } = row.original;

          return (
            <div>
              {row?.original?.title}
              <br />
              <span style={{ color: '#a4a4a4' }}>
                ({sample ? 'Sample' : `${splitAndCapitalize(type)} in “${capitalize(status)}”`})
              </span>
            </div>
          );
        },
      },
      {
        Header: () => <div>Internal Notes</div>,
        accessor: 'notes',
        disableSortBy: true,
      },
      {
        id: 'actions',
        fixedSize: '13rem',
        center: true,
        Header: () => <div>Actions</div>,
        Cell: ({ row }) => (
          <div>
            <Button
              size="small"
              type="default"
              onClick={() => handleClickView({ row })}
              style={{ marginRight: '0.2rem' }}
            >
              View
            </Button>
            <Button size="small" type="default" onClick={() => handleIgnore({ row })} data-tip data-for="ignore-button">
              Ignore
              <Tooltip id="ignore-button" className={s.tooltip}>
                <TooltipText>
                  You can use the ‘Ignore’ button:
                  <ul style={{ paddingInlineStart: '20px' }}>
                    <li>
                      If you’d like to skip the current update.
                      <br />
                      <i>Note that future updates will still appear.</i>
                    </li>
                    <li>If you’ve already made the necessary changes manually.</li>
                  </ul>
                </TooltipText>
              </Tooltip>
            </Button>
          </div>
        ),
      },
    ];
  }, [handleCheckItem, handleClickView, isAllItemsChecked, handleCheckAllItems, items, handleIgnore]);

  useEffect(() => {
    setIsProtectiveWordEntered(inputValue === PROTECTIVE_WORD);
  }, [inputValue]);

  useEffect(() => {
    setIsAnyItemChecked(anyItemCheckedFn(checkedItemsMap.current));
    setIsAllItemsChecked(allItemsCheckedFn(items));
  }, [items]);

  return {
    isLoading,
    isOverwriteBtnActive: isProtectiveWordEntered && isAnyItemChecked,
    inputValue,
    columns,
    items,
    meta,
    handleChangeQueryParams: setQueryParams,
    handleType,
    handleCancel,
    handleConfirm,
  };
}

function anyItemCheckedFn(checkedItemsMap) {
  return pipe(values, any(identity))(checkedItemsMap);
}

function allItemsCheckedFn(items) {
  const isChecked = propEq(true, 'isChecked');

  return all(isChecked)(items);
}

function processItemsWithCheckMap(items, checkingMap) {
  return map(
    when(
      propSatisfies((id) => Boolean(checkingMap[id]), 'id'),
      assoc('isChecked', true),
    ),
    items,
  );
}

function splitAndCapitalize(key) {
  return key.split('_').map(capitalize).join(' ');
}

export function useRerenderableWrapper() {
  const [key, setKey] = useState(0);

  const handleForceRerender = useCallback(() => {
    setKey((val) => val + 1);
  }, []);

  return {
    key,
    handleForceRerender,
  };
}

const onlyIdAndType = pick(['id', 'type']);
